import { HttpInterceptorFn, HttpResponse, HttpHeaders, HttpContextToken } from '@angular/common/http';
import { inject } from '@angular/core';
import { of, tap, switchMap, catchError } from 'rxjs';
import { IndexedDbService } from '../../services/indexeddb/indexed-db.service';

export const USE_CACHE = new HttpContextToken<number>(() => 0);

export interface CachedResponse {
  key: string;
  url: string;
  body: any;
  status: number;
  headers: Record<string, string[]>;
  createdAt: number;
  expiresAt: number;
}
export const indexeddbCacheInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.method.toUpperCase() !== 'GET' || req.context.get(USE_CACHE) === 0) {
    return next(req);
  }

  const dbService = inject(IndexedDbService);
  const cacheKey = req.urlWithParams;
  const maxAge = req.context.get(USE_CACHE) || 60 * 60 * 24;

  return dbService.getItem<CachedResponse>(cacheKey).pipe(
    switchMap((cached) => {
      if (cached) {
        const isFresh = Date.now() < cached.expiresAt;
        if (isFresh) {
          const headers = new HttpHeaders(cached.headers);
          return of(new HttpResponse({ body: cached.body, status: cached.status, url: cached.url, headers }));
        } else {
          dbService.removeItem(cacheKey).subscribe();
        }
      }

      return next(req).pipe(
        tap((event) => {
          if (event instanceof HttpResponse && event.status >= 200 && event.status < 300) {
            const headersMap: Record<string, string[]> = {};
            event.headers.keys().forEach((key) => {
              headersMap[key] = event.headers.getAll(key) ?? [];
            });

            const cachePayload: CachedResponse = {
              key: cacheKey,
              url: cacheKey,
              body: event.body,
              status: event.status,
              headers: headersMap,
              createdAt: Date.now(),
              expiresAt: Date.now() + maxAge * 1000,
            };

            dbService.setItem(cachePayload).subscribe();
          }
        }),
      );
    }),
    catchError((err) => next(req)),
  );
};
